import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { ItIcon } from '../primitives/it-icon'
import type { ItIconName } from '../primitives/it-icon'
import { cn } from '../primitives/util'

/**
 * App button — shadcn/cva architecture (Tailwind utilities over the shadcn
 * token bridge), keeping the original IntegraTrace API so existing call sites
 * are unchanged. `icon`/`iconTrail` take the legacy ItIcon set; `leadingIcon`
 * takes a lucide component.
 */

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--r-sm)] font-medium outline-none transition-[color,background-color,box-shadow,filter] focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow-sm hover:bg-[var(--brand-700)]',
        secondary:
          'bg-[var(--accent-solid)] text-[var(--brand-on-solid)] shadow-sm hover:brightness-110',
        outline: 'border border-border bg-transparent text-foreground hover:bg-secondary',
        ghost: 'bg-transparent text-muted-foreground hover:bg-secondary hover:text-foreground',
        subtle: 'bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground',
        danger: 'bg-destructive text-destructive-foreground shadow-sm hover:brightness-110',
      },
      size: {
        sm: 'h-8 gap-1.5 px-3 text-[13px]',
        md: 'h-9 px-4 text-[13px]',
        lg: 'h-10 px-5 text-sm',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Leading icon — legacy `ItIcon` 24-grid set. */
  icon?: ItIconName
  iconTrail?: ItIconName
  /** Leading icon — lucide component (preferred for new code). */
  leadingIcon?: LucideIcon
  /** Shows a spinner and disables interaction. */
  loading?: boolean
  children?: ReactNode
}

const ICON_SIZE: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 16 }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon,
    iconTrail,
    leadingIcon: LeadingIcon,
    loading = false,
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  const iconSize = ICON_SIZE[size]
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      data-loading={loading || undefined}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <Loader2 className="animate-spin" size={iconSize} aria-hidden="true" />}
      {!loading && LeadingIcon && (
        <LeadingIcon size={iconSize} strokeWidth={1.75} aria-hidden="true" />
      )}
      {!loading && !LeadingIcon && icon && <ItIcon name={icon} size={iconSize} />}
      {children}
      {iconTrail && <ItIcon name={iconTrail} size={iconSize} />}
    </button>
  )
})
